import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { PWAProvider } from "./context/PWAContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { Web3Provider } from "./context/Web3Context";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { AlertsNotifier } from "./components/AlertsNotifier";
import { InstallPWA } from "./components/InstallPWA";
import OfflineIndicator from "./components/OfflineIndicator";
import SupportChat from "./components/SupportChat";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Assets from "./pages/Assets";
import AssetDetail from "./pages/AssetDetail";
import AssetForm from "./pages/AssetForm";
import Insights from "./pages/Insights";
import Opportunities from "./pages/Opportunities";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Plans from "./pages/Plans";
import Marketplace from "./pages/Marketplace";
import Intelligence from "./pages/Intelligence";
import PartnerDashboard from "./pages/PartnerDashboard";
import Provenance from "./pages/Provenance";
import AdminDashboard from "./pages/AdminDashboard";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import Disclaimer from "./pages/Disclaimer";
import InvestorCertification from "./pages/InvestorCertification";
import Risk from "./pages/Risk";
import Timeline from "./pages/Timeline";
import Vault from "./pages/Vault";
import ActionTickets from "./pages/ActionTickets";
import PiiShareView from "./pages/PiiShareView";
import AdminBrokers from "./pages/AdminBrokers";
import BrokerLanding from "./pages/BrokerLanding";
import BrokerJoin from "./pages/BrokerJoin";
import InvestorDeck from "./pages/InvestorDeck";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";
import { FEATURES } from "./config/features";
import { Navigate } from "react-router-dom";

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <CurrencyProvider>
        <PWAProvider>
          <Web3Provider>
          <TooltipProvider delayDuration={300}>
          <BrowserRouter>
            <OfflineIndicator />
            <AlertsNotifier />
            <InstallPWA />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              {/* Public provenance record — login-free, noindexed, direct-link only */}
              <Route path="/provenance/:assetId" element={<Provenance />} />

              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute><Dashboard /></ProtectedRoute>
              } />
              <Route path="/assets" element={
                <ProtectedRoute><Assets /></ProtectedRoute>
              } />
              <Route path="/assets/new" element={
                <ProtectedRoute><AssetForm /></ProtectedRoute>
              } />
              <Route path="/assets/:id" element={
                <ProtectedRoute><AssetDetail /></ProtectedRoute>
              } />
              <Route path="/risk" element={
                <ProtectedRoute><Risk /></ProtectedRoute>
              } />
              <Route path="/timeline" element={
                <ProtectedRoute><Timeline /></ProtectedRoute>
              } />
              <Route path="/vault" element={
                <ProtectedRoute><Vault /></ProtectedRoute>
              } />
              <Route path="/action-queue" element={
                <ProtectedRoute><ActionTickets /></ProtectedRoute>
              } />
              {/* Alias - keep older /tickets links working */}
              <Route path="/tickets" element={<Navigate to="/action-queue" replace />} />
              <Route path="/admin/brokers" element={
                <ProtectedRoute allow={['admin']}><AdminBrokers /></ProtectedRoute>
              } />
              <Route path="/assets/:id/edit" element={
                <ProtectedRoute><AssetForm /></ProtectedRoute>
              } />
              <Route path="/insights" element={
                <ProtectedRoute><Insights /></ProtectedRoute>
              } />
              <Route path="/opportunities" element={
                FEATURES.TOKENISATION_ENABLED
                  ? <ProtectedRoute><Opportunities /></ProtectedRoute>
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/settings" element={
                <ProtectedRoute allow={['admin', 'partner', 'user']}><Settings /></ProtectedRoute>
              } />
              <Route path="/pricing" element={
                <ProtectedRoute><Pricing /></ProtectedRoute>
              } />
              <Route path="/marketplace" element={
                FEATURES.TOKENISATION_ENABLED
                  ? <ProtectedRoute><Marketplace /></ProtectedRoute>
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/intelligence" element={
                <ProtectedRoute><Intelligence /></ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute allow={['admin']}><AdminDashboard /></ProtectedRoute>
              } />
              <Route path="/partner" element={
                <ProtectedRoute allow={['partner', 'admin']}><PartnerDashboard /></ProtectedRoute>
              } />
              <Route path="/onboarding/investor-certification" element={
                FEATURES.TOKENISATION_ENABLED
                  ? <ProtectedRoute allow={['admin', 'partner', 'user']}><InvestorCertification /></ProtectedRoute>
                  : <Navigate to="/dashboard" replace />
              } />

              {/* Legal Pages (Public) */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/share/:token" element={<PiiShareView />} />
              <Route path="/brokers/:slug" element={<BrokerLanding />} />
              <Route path="/brokers/:slug/join" element={<BrokerJoin />} />
              <Route path="/brokers/apply" element={<BrokerJoin />} />
              <Route path="/investor-deck" element={<InvestorDeck />} />
              <Route path="/plans" element={<Plans />} />

              {/* Global 404 fallback — must remain last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <SupportChat />
          </BrowserRouter>
          </TooltipProvider>
          </Web3Provider>
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: {
                background: '#0F0F0F',
                border: '1px solid #262626',
                color: '#F5F5F5',
              },
            }}
          />
        </PWAProvider>
      </CurrencyProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
